import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {
  fetchCrmAccountabilityPolicies,
  getFriendlyApiError,
  updateCrmAccountabilityPolicies,
} from "../../../../../services/crm";

const STAGES = [
  { key: "commonPool", label: "All Leads" },
  { key: "myLeads", label: "My Leads" },
  { key: "opportunity", label: "Opportunity" },
  { key: "waiting", label: "Waiting" },
  { key: "presentation", label: "Presentation" },
  { key: "estimate", label: "Estimate" },
  { key: "salesOrder", label: "Sales Order" },
  { key: "win", label: "WIN", terminal: true },
  { key: "loss", label: "LOSS", terminal: true },
];

const DEFAULT_ESTIMATE_BUCKETS = [
  { maxDays: 7, label: "0-7" },
  { maxDays: 14, label: "8-14" },
  { maxDays: 30, label: "15-30" },
  { maxDays: null, label: "30+" },
];

const ACTIVITY_TYPES = [
  { value: "follow_up", label: "Follow up" },
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "todo", label: "To-do" },
];

const GRID_COLUMNS = "minmax(132px, 1.25fr) repeat(3, minmax(106px, 0.8fr)) minmax(150px, 1fr) minmax(140px, 1fr) minmax(126px, 0.85fr) minmax(116px, 0.75fr)";

function defaultPolicy(stage) {
  return {
    stageKey: stage.key,
    entityType: "all",
    stageTargetAgeDays: stage.terminal ? null : 14,
    yellowAgeDays: stage.terminal ? null : 14,
    redAgeDays: stage.terminal ? null : 30,
    requiresNextActivity: !stage.terminal,
    estimateAgeBuckets: DEFAULT_ESTIMATE_BUCKETS,
    defaultActivityType: "follow_up",
    defaultDueOffsetHours: 24,
    managerEscalationRules: null,
    automationEnabled: false,
    isActive: true,
    source: "default",
  };
}

const defaultPolicies = () => STAGES.map(defaultPolicy);

function normalizePolicies(rows = []) {
  const allEntityRows = new Map(
    rows
      .filter((row) => String(row.entityType || "all").toLowerCase() === "all")
      .map((row) => [row.stageKey, row])
  );
  return STAGES.map((stage) => ({
    ...defaultPolicy(stage),
    ...(allEntityRows.get(stage.key) || {}),
    stageKey: stage.key,
    entityType: "all",
  }));
}

function numericValue(value) {
  return value === null || value === undefined ? "" : value;
}

function normalizedInteger(value) {
  if (value === "" || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isInteger(numeric) ? numeric : null;
}

function validatePolicies(policies) {
  for (const policy of policies) {
    const fields = [
      ["Target age", policy.stageTargetAgeDays],
      ["Yellow warning", policy.yellowAgeDays],
      ["Red alert", policy.redAgeDays],
      ["Due offset", policy.defaultDueOffsetHours],
    ];
    for (const [label, value] of fields) {
      if (value !== null && value !== "" && (!Number.isInteger(Number(value)) || Number(value) < 0)) {
        return `${label} for ${policy.stageKey} must be a whole number of zero or more.`;
      }
    }
    if (
      policy.yellowAgeDays !== null && policy.yellowAgeDays !== "" &&
      policy.redAgeDays !== null && policy.redAgeDays !== "" &&
      Number(policy.redAgeDays) < Number(policy.yellowAgeDays)
    ) {
      return `Red alert days cannot be lower than yellow warning days for ${policy.stageKey}.`;
    }
  }
  return "";
}

function NumberField({ label, value, onChange }) {
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={numericValue(value)}
      onChange={(event) => onChange(event.target.value)}
      inputProps={{ min: 0, step: 1 }}
      fullWidth
    />
  );
}

export default function AccountabilitySettingsDialog({ open, instanceUniqueId, canEdit, onClose, onSaved }) {
  const [policies, setPolicies] = useState(defaultPolicies);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const stageByKey = useMemo(() => new Map(STAGES.map((stage) => [stage.key, stage])), []);

  const loadPolicies = useCallback(async () => {
    if (!instanceUniqueId) return;
    setLoading(true);
    setError("");
    setSaved(false);
    try {
      const result = await fetchCrmAccountabilityPolicies(instanceUniqueId);
      setPolicies(normalizePolicies(result?.policies));
    } catch (requestError) {
      setError(getFriendlyApiError(requestError, "Could not load CRM accountability settings."));
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    if (open) loadPolicies();
  }, [loadPolicies, open]);

  const updatePolicy = (stageKey, field, value) => {
    setSaved(false);
    setPolicies((current) => current.map((policy) => (
      policy.stageKey === stageKey ? { ...policy, [field]: value } : policy
    )));
  };

  const restoreDefaults = () => {
    setPolicies(defaultPolicies());
    setError("");
    setSaved(false);
  };

  const savePolicies = async () => {
    const validationError = validatePolicies(policies);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const payload = policies.map((policy) => ({
        ...policy,
        stageTargetAgeDays: normalizedInteger(policy.stageTargetAgeDays),
        yellowAgeDays: normalizedInteger(policy.yellowAgeDays),
        redAgeDays: normalizedInteger(policy.redAgeDays),
        defaultDueOffsetHours: normalizedInteger(policy.defaultDueOffsetHours) ?? 24,
        automationEnabled: policy.automationEnabled === true,
      }));
      const result = await updateCrmAccountabilityPolicies(instanceUniqueId, payload);
      setPolicies(normalizePolicies(result?.policies));
      setSaved(true);
      onSaved?.();
    } catch (requestError) {
      setError(getFriendlyApiError(requestError, "Could not save CRM accountability settings."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>CRM Accountability Settings</Typography>
            <Typography variant="body2" color="text.secondary">Stage warning thresholds and next activity defaults</Typography>
          </Box>
          <Button size="small" color="inherit" startIcon={<CloseRoundedIcon />} onClick={onClose} disabled={saving}>Close</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.25, md: 2 } }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 260 }}><CircularProgress size={28} /></Stack>
        ) : (
          <Stack spacing={1.5}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {saved ? <Alert severity="success">Accountability settings saved. The CRM board has been refreshed.</Alert> : null}
            <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
              <Box sx={{ minWidth: 1120 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GRID_COLUMNS,
                    gap: 1,
                    alignItems: "center",
                    px: 1.25,
                    py: 1,
                    backgroundColor: "#f8fafc",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {["Stage", "Target age", "Yellow warning", "Red alert", "Requires next activity", "Default activity", "Due offset hours", "Automation"].map((label) => (
                    <Typography key={label} variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>{label}</Typography>
                  ))}
                </Box>
                {policies.map((policy) => {
                  const stage = stageByKey.get(policy.stageKey) || { label: policy.stageKey };
                  return (
                    <Box
                      key={policy.stageKey}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: GRID_COLUMNS,
                        gap: 1,
                        alignItems: "center",
                        px: 1.25,
                        py: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:last-child": { borderBottom: 0 },
                      }}
                    >
                      <Stack spacing={0.35} alignItems="flex-start">
                        <Typography variant="body2" sx={{ fontWeight: 850 }}>{stage.label}</Typography>
                        <Chip size="small" variant="outlined" label={policy.source === "db" ? "Custom" : "Default"} sx={{ height: 19, fontSize: 10 }} />
                      </Stack>
                      <NumberField label="Days" value={policy.stageTargetAgeDays} onChange={(value) => updatePolicy(policy.stageKey, "stageTargetAgeDays", value)} />
                      <NumberField label="Days" value={policy.yellowAgeDays} onChange={(value) => updatePolicy(policy.stageKey, "yellowAgeDays", value)} />
                      <NumberField label="Days" value={policy.redAgeDays} onChange={(value) => updatePolicy(policy.stageKey, "redAgeDays", value)} />
                      <FormControlLabel
                        control={<Switch size="small" checked={Boolean(policy.requiresNextActivity)} onChange={(event) => updatePolicy(policy.stageKey, "requiresNextActivity", event.target.checked)} />}
                        label={policy.requiresNextActivity ? "Required" : "Not required"}
                        sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                      />
                      <TextField
                        select
                        size="small"
                        label="Type"
                        value={policy.defaultActivityType || "follow_up"}
                        onChange={(event) => updatePolicy(policy.stageKey, "defaultActivityType", event.target.value)}
                        SelectProps={{ native: true }}
                        fullWidth
                      >
                        {ACTIVITY_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                      </TextField>
                      <NumberField label="Hours" value={policy.defaultDueOffsetHours} onChange={(value) => updatePolicy(policy.stageKey, "defaultDueOffsetHours", value)} />
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Switch
                          size="small"
                          checked={policy.automationEnabled === true}
                          disabled={!canEdit || stage.terminal}
                          onChange={(event) => updatePolicy(policy.stageKey, "automationEnabled", event.target.checked)}
                        />
                        <Chip
                          size="small"
                          label={policy.automationEnabled ? "Ezee" : "Off"}
                          color={policy.automationEnabled ? "primary" : "default"}
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10 }}
                        />
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Alert severity="info">
              Automation is off by default. When enabled for a stage, the same accountability warnings feed Ezee's durable attention queue. Ezee may recommend or prepare the configured follow-up, but customer-facing actions still require the normal approval/confirmation path.
            </Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button startIcon={<RestartAltRoundedIcon />} onClick={restoreDefaults} disabled={loading || saving || !canEdit}>Restore defaults</Button>
        <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={savePolicies} disabled={loading || saving || !canEdit}>
          {saving ? "Saving..." : "Save settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
